import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useConsent } from "@/hooks/useConsent";
import { useLang } from "@/i18n";

/**
 * LGPD (Lei nº 13.709/2018) — Brazil-adapted consent banner.
 * Three categories: necessary (always) · analytics · marketing.
 *
 * Analytics / PostHog scripts in index.html check `window.__iagConsent`
 * before loading. The `iag:consent:updated` event is dispatched whenever
 * the user changes their preference.
 */
export const ConsentBanner = () => {
  const { decided, analytics, marketing, acceptAll, rejectAll, savePartial } = useConsent();
  const { l } = useLang();
  const [showDetails, setShowDetails] = useState(false);
  const [a, setA] = useState(analytics);
  const [m, setM] = useState(marketing);

  if (decided) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-0 left-0 right-0 z-[80] px-4 pb-4 md:pb-6 md:px-6 pointer-events-none"
        data-testid="consent-banner"
        role="dialog"
        aria-live="polite"
        aria-label="Privacy consent"
      >
        <div className="pointer-events-auto max-w-[1100px] mx-auto bg-paper border border-ink/15 shadow-[6px_12px_44px_rgba(27,27,24,0.14)]">
          <div className="p-5 md:p-7">
            {!showDetails ? (
              <div className="flex flex-col md:flex-row md:items-start gap-5 md:gap-8">
                <div className="flex-1">
                  <p className="font-serif text-xl md:text-2xl text-ink leading-snug mb-2">
                    Cookies &amp; privacidade
                  </p>
                  <p className="font-body text-sm md:text-base text-ink-soft leading-relaxed max-w-2xl">
                    We use cookies to run the site, understand how it&rsquo;s used and
                    improve your experience. You can accept all, reject non-essential
                    ones or choose exactly what you allow. See our{" "}
                    <a href={l("/privacy")} className="underline decoration-terracotta/60 hover:text-terracotta">
                      Privacy Policy
                    </a>{" "}
                    for details (LGPD - Lei n&ordm; 13.709/2018).
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 shrink-0 self-stretch md:self-start">
                  <button
                    onClick={() => setShowDetails(true)}
                    data-testid="consent-customize"
                    className="font-body text-xs uppercase tracking-[0.16em] px-5 py-3 border border-ink/40 text-ink hover:border-terracotta hover:text-terracotta transition-colors"
                  >
                    Customise
                  </button>
                  <button
                    onClick={rejectAll}
                    data-testid="consent-reject"
                    className="font-body text-xs uppercase tracking-[0.16em] px-5 py-3 border border-ink/40 text-ink hover:border-terracotta hover:text-terracotta transition-colors"
                  >
                    Reject non-essential
                  </button>
                  <button
                    onClick={acceptAll}
                    data-testid="consent-accept-all"
                    className="bg-ink text-paper px-6 py-3 font-body text-xs uppercase tracking-[0.22em]"
                  >
                    Accept all
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="font-serif text-xl md:text-2xl text-ink leading-snug mb-4">
                  Cookie preferences
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start justify-between gap-4 py-3 border-b border-ink/10">
                    <div>
                      <p className="font-body font-medium text-ink">Necessary</p>
                      <p className="font-body text-sm text-ink-soft">
                        Required for the site to work. Always on.
                      </p>
                    </div>
                    <span className="font-hand text-terracotta text-lg mt-1">Always active</span>
                  </li>
                  <li className="flex items-start justify-between gap-4 py-3 border-b border-ink/10">
                    <div>
                      <p className="font-body font-medium text-ink">Analytics</p>
                      <p className="font-body text-sm text-ink-soft">
                        Google Analytics + PostHog. Helps us understand which pages help visitors most.
                      </p>
                    </div>
                    <label className="flex items-center cursor-pointer mt-1 shrink-0">
                      <input
                        type="checkbox"
                        checked={a}
                        onChange={(e) => setA(e.target.checked)}
                        data-testid="consent-toggle-analytics"
                        className="w-5 h-5 accent-mustard"
                      />
                    </label>
                  </li>
                  <li className="flex items-start justify-between gap-4 py-3 border-b border-ink/10">
                    <div>
                      <p className="font-body font-medium text-ink">Marketing</p>
                      <p className="font-body text-sm text-ink-soft">
                        Session recording + retargeting. Helps improve marketing pages.
                      </p>
                    </div>
                    <label className="flex items-center cursor-pointer mt-1 shrink-0">
                      <input
                        type="checkbox"
                        checked={m}
                        onChange={(e) => setM(e.target.checked)}
                        data-testid="consent-toggle-marketing"
                        className="w-5 h-5 accent-mustard"
                      />
                    </label>
                  </li>
                </ul>
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    onClick={rejectAll}
                    className="font-body text-xs uppercase tracking-[0.16em] px-5 py-3 border border-ink/40 text-ink hover:border-terracotta hover:text-terracotta transition-colors"
                  >
                    Reject non-essential
                  </button>
                  <button
                    onClick={() => savePartial({ analytics: a, marketing: m })}
                    data-testid="consent-save"
                    className="bg-ink text-paper px-6 py-3 font-body text-xs uppercase tracking-[0.22em]"
                  >
                    Save preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
